import { GridItem, Heading, Box, Icon, Text, Flex, Divider, SimpleGrid, Image, Avatar, Button } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { IconLetter, IconPen, IconPhone, IconUser } from 'assets/icons';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import { CUSTOM_FIELD_DATA_TYPES } from 'global/options';
import { routePaths } from 'global/routePaths';
import { useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useFetchJobById } from 'hooks/jobs/useFetchJobById';
import { useJobsTasksStore } from 'store/useJobsTasksStore';
import { to12Hour } from 'utils/format';
import { isEmptyObject, isEmptyObjectValues } from 'utils/helpers';

import JobDetailMap from './JobDetailMap';

const JobDetail: React.FC<unknown> = () => {
  const { t } = useTranslation(['jobsTasks', 'common']);

  const { id }: any = useParams();
  const navigate = useNavigate();

  const { data, status } = useFetchJobById(id!);
  const { jobsTasksHasRedirected } = useJobsTasksStore(state => state);

  const { isFetching: isCustomFieldFetching, data: customFieldData } = useFetchPaginatedCustomFields({
    page: 1,
    page_size: 500,
    module: 'jobs',
    active: '1',
  });

  if (status === 'loading' || isCustomFieldFetching) return <LogoLoader />;

  return (
    <Box bg="gray.100" height="100%">
      <Box p="4" bg="white">
        {/* contact Information */}
        <Flex justifyContent="space-between" alignItems="center">
          <Title backBtnAfterOnClick={jobsTasksHasRedirected}>{data?.payload?.name}</Title>
          <Button
            variant="ghost"
            leftIcon={
              <Icon
                as={IconPen}
                sx={{
                  path: {
                    fill: 'primary.500',
                  },
                }}
                boxSize="12px"
              />
            }
            color="primary.500"
            fontWeight={700}
            onClick={() => navigate(routePaths.jobsAndTasks.editJob.replace(':id', id))}
          >
            {t('common:edit')}
          </Button>
        </Flex>
        {data?.payload?.contactRequired ? (
          <>
            <Heading
              as="h6"
              mt="10"
              mb="3"
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
            >
              {t('jobsTasks:contactDetails')}
            </Heading>
            <Flex direction="row" alignItems="center" justifyContent="space-between" wrap="wrap">
              <Flex alignItems="center" mr="5">
                <Icon as={IconUser} mr="1" sx={{ '& path': { fill: 'gray.400' } }} />
                <Text fontWeight="600">{data?.payload?.contactInfo?.contactName}</Text>
              </Flex>
              <Flex alignItems="center" mr="5">
                <Icon as={IconLetter} mr="1" />
                <Text fontWeight="600">{data?.payload?.contactInfo?.contactEmail}</Text>
              </Flex>
              <Flex alignItems="center">
                <Icon as={IconPhone} mr="1" />
                <Text fontWeight="600">{data?.payload?.contactInfo?.contactPhone}</Text>
              </Flex>
            </Flex>
          </>
        ) : null}

        {/* location */}
        <Divider my="6" />
        <SimpleGrid columns={{ lg: 2 }} spacingX={{ lg: 5 }} spacingY={5}>
          <Text>
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              {t('jobsTasks:location')}
            </Heading>
            {!isEmptyObjectValues(data?.payload?.address)
              ? Object.values(data?.payload?.address).map((item: any, index: number, orgArray: Array<any>) => (
                  <Text as="span" key={index}>
                    {item}
                    {index + 1 !== orgArray.length ? ', ' : ''}
                  </Text>
                ))
              : t('jobsTasks:noLocationDetailsFound')}
          </Text>
          {data?.payload.longitude && data?.payload.latitude && <JobDetailMap data={data} />}
        </SimpleGrid>
        <Divider my="6" />

        {/* Job costing and tracking  */}
        <SimpleGrid columns={{ lg: 2 }} spacingX={{ lg: 5 }} spacingY={5}>
          <GridItem>
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              {t('jobsTasks:jobCostingDetails')}
            </Heading>
            <Text>
              {t('jobsTasks:hourlyRate')}:{' '}
              <Text as="span" fontWeight="semibold">
                {data?.payload?.rate || '- '}
              </Text>
            </Text>
          </GridItem>

          <GridItem>
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              {t('jobsTasks:trackingPreferences')}
            </Heading>
            <Text mb="2">
              {t('jobsTasks:mileageTracking')}:{' '}
              {data?.payload?.trackMileage ? (
                <Text as="span" color="success.500" fontWeight="semibold" textTransform="uppercase">
                  {t('common:yes')}
                </Text>
              ) : (
                <Text as="span" color="secondary.500" fontWeight="semibold" textTransform="uppercase">
                  {t('common:no')}
                </Text>
              )}
            </Text>
            <Text mb="2">
              {t('jobsTasks:requireGeofencing')}:{' '}
              {data?.payload?.requireGeofence ? (
                <Text as="span" color="success.500" fontWeight="semibold" textTransform="uppercase">
                  {t('common:yes')}
                </Text>
              ) : (
                <Text as="span" color="secondary.500" fontWeight="semibold" textTransform="uppercase">
                  {t('common:no')}
                </Text>
              )}
            </Text>
            <Text mb="2">
              {t('jobsTasks:distanceToClockIn')}:{' '}
              <Text as="span" fontWeight="semibold">
                {data?.payload?.radius || '- '}
              </Text>
            </Text>
            {data?.payload?.jobCode ? (
              <Text>
                {t('jobsTasks:jobCode')}:{' '}
                <Text as="span" fontWeight="semibold">
                  {data?.payload?.jobCode || '- '}
                </Text>
              </Text>
            ) : null}
          </GridItem>
        </SimpleGrid>

        {Array.isArray(data?.payload?.assigned_users) && data?.payload?.assigned_users?.length ? (
          <>
            <Divider my="6" />
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              {t('jobsTasks:assignedTo')}
            </Heading>
            <SimpleGrid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
              {data.payload?.assigned_users.map((item: any, index: number) => (
                <Flex alignItems="center" key={index}>
                  <Image
                    src={item?.employee_image || ''}
                    fallback={
                      <Avatar
                        name={item?.firstName + ' ' + item?.lastName}
                        boxSize="24px"
                        sx={{ '& div': { fontSize: 'xs' } }}
                        borderRadius="md"
                      />
                    }
                    alt={item?.firstName}
                    boxSize="24px"
                    objectFit="cover"
                    borderRadius="md"
                  />
                  <Text ml="1">
                    {item?.firstName} {item?.lastName}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </>
        ) : null}

        {/* description */}
        {data?.payload?.description ? (
          <>
            <Divider my="6" />
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              {t('common:description')}
            </Heading>
            <Text>{data?.payload?.description}</Text>
          </>
        ) : null}

        {/* Custom Fields */}
        {!isEmptyObject(data?.payload?.custom_fields || {}) ? (
          <Box>
            <Divider my="6" />
            <Heading as="h6" mb="3" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
              Custom Fields
            </Heading>
            <SimpleGrid columns={{ md: 2 }} spacingX={{ base: 6, md: 12 }}>
              {customFieldData?.payload.map(field => {
                let customFieldValue = data?.payload?.custom_fields[field.id];

                if (field.field_type === CUSTOM_FIELD_DATA_TYPES.TIME_PICKER) {
                  customFieldValue = to12Hour(customFieldValue);
                }

                if (customFieldValue) {
                  return (
                    <Flex mb="2" key={field.id}>
                      <Text as="span">{field.field_name}:</Text>
                      <Text as="span" fontWeight="600" ml="1">
                        {Array.isArray(customFieldValue) ? customFieldValue.join(', ') : customFieldValue}
                      </Text>
                    </Flex>
                  );
                }

                return null;
              })}
            </SimpleGrid>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default JobDetail;
